import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { RichText } from '../../components/RichText';
import { formatDateTime } from '../../i18n';
import { type TicketAccess, replyWithTicketAccess } from './accessApi';

/**
 * Ein Vorgang, geoeffnet ueber Adresse und Nummer -- F4-35.
 *
 * **Dieselbe Abfolge wie im angemeldeten Portal** (`PortalTicketDetail`):
 * Sachverhalt, Antwortfeld, Verlauf mit dem neuesten Beitrag zuerst. Zwei
 * verschiedene Anordnungen fuer denselben Inhalt waeren fuer den Kunden zwei
 * verschiedene Anwendungen -- und er weiss nicht, ueber welchen der beiden
 * Wege er gerade hereingekommen ist.
 *
 * **Eigene Datei statt eines Schalters in `PortalTicketDetail`.** Die
 * Datenquelle ist eine andere (ein Merkmal statt einer Sitzung), der
 * Ausschnitt ist enger, und es gibt keine Liste, zu der man zurueckkehrt.
 * Ein gemeinsamer Bauteil mit zwei Betriebsarten waere an jeder zweiten
 * Zeile eine Fallunterscheidung.
 *
 * **Was hier nicht gefiltert wird.** Der Server liefert ausschliesslich
 * oeffentliche Beitraege (F4-23). Ein zweiter Filter im Browser waere kein
 * zusaetzlicher Schutz -- er waere die Stelle, an der jemand spaeter glaubt,
 * der erste sei entbehrlich.
 */
export function TicketAccessView({
  access,
  onChanged,
  onClose,
}: {
  access: TicketAccess;
  onChanged: (next: TicketAccess) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();

  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      // Als Absatz und nicht als Rohtext: Der Server entschaerft das HTML,
      // aber Zeilenumbrueche gingen ohne Auszeichnung verloren.
      onChanged(await replyWithTicketAccess(access.token, `<p>${escapeHtml(message)}</p>`));
      setMessage('');
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={
          <>
            {access.ticket.title}{' '}
            <Badge tone="info" dot>
              {access.ticket.state_name}
            </Badge>
          </>
        }
        subtitle={access.ticket.ticket_number}
        action={
          <Button variant="ghost" onClick={onClose}>
            {t('portal.access.leave')}
          </Button>
        }
      />

      <section className="card">
        <h2>{t('portal.description')}</h2>
        {access.ticket.description_html ? (
          <RichText html={access.ticket.description_html} />
        ) : (
          <p className="prose">{access.ticket.description || '—'}</p>
        )}
      </section>

      <section className="card">
        <h2>{t('portal.conversation')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="access-reply">
            {t('portal.reply')}
          </label>
          <textarea
            id="access-reply"
            className="field__control field__control--area"
            rows={4}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
          />
        </div>

        {error ? <ErrorNotice error={error} /> : null}

        <Button
          variant="primary"
          disabled={!message.trim()}
          busy={busy}
          onClick={() => {
            void send();
          }}
        >
          {t('portal.send')}
        </Button>

        {/* Der Server liefert aufsteigend; gedreht wird in der Anzeige, damit
            der letzte Stand oben steht. */}
        <ul className="timeline">
          {[...access.articles].reverse().map((article) => (
            <li key={article.id} className="timeline__entry timeline__entry--public">
              <div className="timeline__head">
                <span className="timeline__who">
                  {article.from_customer ? t('portal.access.fromYou') : t('portal.access.fromUs')}
                </span>
                <span className="timeline__time">
                  {formatDateTime(article.created_at, i18n.language)}
                </span>
              </div>
              <p className="prose">{article.body_text}</p>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * Maskiert Text, bevor er in HTML eingesetzt wird.
 *
 * Wortgleich mit `PortalTicketDetail`: Der Server entschaerft ohnehin --
 * aber er entfernt dabei, was wie ein Element aussieht. Wer "<Fehler>"
 * schreibt, bekaeme sonst eine Rueckmeldung ohne dieses Wort und keinen
 * Hinweis, warum.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br />');
}
